import { type FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

import { cn } from "@/shared/lib/utils";

export type JourneyStepStatus = "complete" | "current" | "upcoming" | "locked";

export interface JourneyStep {
  id: string;
  label: string;
  status: JourneyStepStatus;
}

interface JourneyProgressBarProps {
  steps: JourneyStep[];
  paused?: boolean;
  className?: string;
}

const ANIMATION_START_DELAY_MS = 300;
const LINE_FILL_MS = 600;

interface DisplayedProgress {
  completeCount: number;
  currentIndex: number;
}

function getTargetIndex(steps: JourneyStep[]): number {
  const currentIndex = steps.findIndex((s) => s.status === "current");
  return currentIndex >= 0 ? currentIndex : steps.filter((s) => s.status === "complete").length;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function useAnimatedProgress(targetIndex: number, paused: boolean): DisplayedProgress {
  const [displayed, setDisplayed] = useState<DisplayedProgress>({
    completeCount: targetIndex,
    currentIndex: targetIndex,
  });
  const displayedIndexRef = useRef(targetIndex);

  useEffect(() => {
    const fromIndex = displayedIndexRef.current;
    if (paused || targetIndex === fromIndex) {
      return;
    }

    const finish = () => {
      displayedIndexRef.current = targetIndex;
      setDisplayed({ completeCount: targetIndex, currentIndex: targetIndex });
    };

    if (targetIndex < fromIndex || prefersReducedMotion()) {
      finish();
      return;
    }

    // Only mark the target as reached once the animation finishes, so pausing
    // mid-animation leaves it in place and unpausing plays it again.
    const completeTimer = setTimeout(
      () => setDisplayed((prev) => ({ ...prev, completeCount: targetIndex })),
      ANIMATION_START_DELAY_MS,
    );
    const currentTimer = setTimeout(finish, ANIMATION_START_DELAY_MS + LINE_FILL_MS);
    return () => {
      clearTimeout(completeTimer);
      clearTimeout(currentTimer);
    };
  }, [targetIndex, paused]);

  return displayed;
}

function getDisplayedStatus(index: number, progress: DisplayedProgress): JourneyStepStatus {
  if (index < progress.completeCount) {
    return "complete";
  }
  return index === progress.currentIndex ? "current" : "upcoming";
}

const StepDot: FC<{ status: JourneyStepStatus; number: number }> = ({ status, number }) => {
  const isComplete = status === "complete";
  const isCurrent = status === "current";

  return (
    <div className="relative size-6 shrink-0">
      <div
        className={cn(
          "absolute -inset-1 rounded-full bg-brand-subtle transition-transform duration-300 motion-reduce:transition-none",
          isCurrent ? "scale-100" : "scale-0",
        )}
      />
      <div
        className={cn(
          "relative flex size-6 items-center justify-center rounded-full border-2 transition-colors duration-300 motion-reduce:transition-none",
          isComplete && "border-brand bg-brand",
          isCurrent && "border-brand bg-white",
          !isComplete && !isCurrent && "border-strong bg-white",
        )}
      >
        {isComplete ? (
          <Check
            className="size-3.5 text-white animate-in zoom-in-50 duration-300 motion-reduce:animate-none"
            strokeWidth={3}
          />
        ) : isCurrent ? (
          <div className="size-2.5 rounded-full bg-brand animate-in zoom-in-50 duration-300 motion-reduce:animate-none" />
        ) : (
          <span className="text-xs font-semibold leading-none tabular-nums text-tertiary">
            {number}
          </span>
        )}
      </div>
    </div>
  );
};

function useDotCenters(stepCount: number) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [centers, setCenters] = useState<number[]>([]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    const measure = () => {
      const containerLeft = container.getBoundingClientRect().left;
      setCenters(
        dotRefs.current.slice(0, stepCount).map((dot) => {
          const rect = dot?.getBoundingClientRect();
          return rect ? rect.left + rect.width / 2 - containerLeft : 0;
        }),
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [stepCount]);

  return { containerRef, dotRefs, centers };
}

export const JourneyProgressBar: FC<JourneyProgressBarProps> = ({
  steps,
  paused = false,
  className,
}) => {
  const progress = useAnimatedProgress(getTargetIndex(steps), paused);
  const activeIndex = Math.min(progress.currentIndex, steps.length - 1);
  const { containerRef, dotRefs, centers } = useDotCenters(steps.length);
  const lastIndex = steps.length - 1;
  const hasMeasured = centers.length === steps.length && steps.length > 1;
  const trackStart = hasMeasured ? centers[0] : 0;
  const trackWidth = hasMeasured ? centers[lastIndex] - trackStart : 0;
  const filledWidth = hasMeasured
    ? centers[Math.min(progress.completeCount, lastIndex)] - trackStart
    : 0;

  return (
    <div className={cn("w-full", className)}>
      <div className="flex flex-col gap-2 sm:hidden">
        <p className="text-sm text-secondary">
          Step {activeIndex + 1} of {steps.length}
          {steps[activeIndex] ? ` · ${steps[activeIndex].label}` : ""}
        </p>
        <div className="flex gap-1">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={cn(
                "h-2 flex-1 rounded-full border transition-colors duration-300 motion-reduce:transition-none",
                index <= progress.currentIndex ? "border-brand bg-brand" : "border-strong bg-white",
              )}
            />
          ))}
        </div>
      </div>

      <div ref={containerRef} className="relative hidden sm:block">
        {hasMeasured && (
          <>
            <div
              className="absolute top-[11px] border-t-2 border-strong"
              style={{ left: trackStart, width: trackWidth }}
            />
            <div
              className="absolute top-[11px] border-t-2 border-brand transition-[width] ease-out motion-reduce:transition-none"
              style={{
                left: trackStart,
                width: filledWidth,
                transitionDuration: `${LINE_FILL_MS}ms`,
              }}
            />
          </>
        )}
        <ol className="relative flex items-start justify-between">
          {steps.map((step, index) => {
            const status = getDisplayedStatus(index, progress);
            const isEnd = index === 0 || index === lastIndex;

            return (
              <li
                key={step.id}
                aria-current={status === "current" ? "step" : undefined}
                className={cn("relative flex flex-col items-center", isEnd ? "gap-3" : "w-6")}
              >
                <div
                  ref={(el) => {
                    dotRefs.current[index] = el;
                  }}
                >
                  <StepDot status={status} number={index + 1} />
                </div>
                <span
                  className={cn(
                    "whitespace-nowrap text-sm transition-colors duration-300 motion-reduce:transition-none",
                    !isEnd && "absolute left-1/2 top-9 -translate-x-1/2",
                    status === "current" && "font-semibold text-brand",
                    status === "complete" && "font-medium text-primary",
                    status === "upcoming" && "font-medium text-secondary",
                  )}
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};
